import useBrainSearchBot from "@/admin/integrations/chat-bot/util/useBrainSearchBot"
import useStartNewBrainSearch from "@/admin/integrations/chat-bot/util/useStartNewBrainSearch"
import BrainSearchInput from "@/brain-search/internal/BrainSearchInput"
import BrainSearchPageHeader from "@/brain-search/internal/BrainSearchPageHeader"
import BrainSearchProcessingWarning from "@/brain-search/internal/BrainSearchProcessingWarning"
import BrainSearchPreviewHeader from "@/brain-search/internal/drawer/BrainSearchPreviewHeader"
import BrainSearchSidePanelToggle from "@/brain-search/internal/drawer/BrainSearchSidePanelToggle"
import PastUserBrainSearches from "@/brain-search/internal/PastUserBrainSearches"
import { useBrainSearchWidth } from "@/brain-search/internal/utils/BrainSearchUtils"
import { BrainSearchLandingPageQuery } from "@/brain-search/internal/__generated__/BrainSearchLandingPageQuery.graphql"
import { useAuthUser } from "@/core/context/AuthUserContext"
import { useGlobalDrawer } from "@/core/context/GlobalDrawerProvider"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import styleIf from "@assets/style/util/styleIf"
import { DiscoIcon, DiscoIconButton, DiscoSpinner, DiscoText } from "@disco-ui"
import usePermissions from "@utils/hook/usePermissions"
import classNames from "classnames"
import { graphql } from "react-relay"

function BrainSearchLandingPage() {
  const { authUser } = useAuthUser()
  const drawer = useGlobalDrawer("brainSearch")
  const { isSidePanel, isWide } = useBrainSearchWidth()
  const classes = useStyles({ isSidePanel, isWide })
  const brainSearchBot = useBrainSearchBot()!

  const { node } = Relay.useSkippableLazyLoadQuery<BrainSearchLandingPageQuery>(
    graphql`
      query BrainSearchLandingPageQuery($id: ID!) {
        node(id: $id) {
          __typename
          ... on ContentUsage {
            id
            content {
              id
              hasUnprocessedMediaAssets
            }
            ...usePermissionsFragment
            ...BrainSearchInput_ContentUsageFragment
            ...BrainSearchSidePanelToggle_ContentUsageFragment
            ...PastUserBrainSearchesContentUsageFragment
          }
        }
      }
    `,
    { id: drawer.params.sourceId || "" },
    { skip: !drawer.params.sourceId }
  )
  const sourceContentUsage = Relay.narrowNodeType(node, "ContentUsage")
  const permissions = usePermissions(sourceContentUsage)
  const canManagePrimaryContent = permissions.has("content.manage")

  const [form, handleSubmit] = useStartNewBrainSearch({
    isSidePanel,
    contentUsageId: sourceContentUsage?.id,
  })

  return (
    <>
      {renderTopButtons()}
      <div className={classNames(classes.root, classes.fadeIn)}>
        <div className={classes.container}>
          {canManagePrimaryContent &&
            sourceContentUsage?.content.hasUnprocessedMediaAssets && (
              <BrainSearchProcessingWarning />
            )}
          <div className={classes.headerContainer}>
            {isSidePanel && sourceContentUsage ? (
              <PastUserBrainSearches contentUsageKey={sourceContentUsage} />
            ) : (
              <>
                <DiscoText variant={"heading-md-700"} color={"text.primary"}>
                  {authUser?.firstName ? `Hello, ${authUser?.firstName}.` : "Hello."}
                </DiscoText>
                <DiscoText
                  variant={"body-md"}
                  color={"text.secondary"}
                  marginBottom={isSidePanel ? 0 : 2}
                  className={classes.subHeader}
                >
                  {"What would you like to learn today?"}
                </DiscoText>
              </>
            )}
          </div>

          <div className={classes.inputContainer}>
            <BrainSearchInput
              placeholder={"Ask a question..."}
              onChange={handleChangeQuery}
              onSubmit={handleSubmit}
              clearOnSubmit={false}
              variant={isWide ? "raised" : "flat"}
              contentUsageKey={sourceContentUsage}
              enableSuggestions={true}
              suggestionsPosition={isWide ? "below" : "above"}
              isSidePanel={isSidePanel}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
          </div>
        </div>
      </div>
    </>
  )

  function handleChangeQuery(text: string) {
    form.state.title = text
  }

  function renderTopButtons() {
    if (brainSearchBot.status === "paused") return <BrainSearchPreviewHeader />
    if (isWide)
      return (
        <div className={classes.topButtons}>
          <BrainSearchSidePanelToggle contentUsageKey={sourceContentUsage} />
          <DiscoIconButton
            onClick={drawer.close}
            testid={"BrainSearchPageHeader.close-button"}
          >
            <DiscoIcon icon={"close"} />
          </DiscoIconButton>
        </div>
      )
    return <BrainSearchPageHeader />
  }
}

type StyleProps = {
  isSidePanel: boolean
  isWide: boolean
}

const useStyles = makeUseStyles((theme) => ({
  topButtons: {
    position: "absolute",
    top: theme.spacing(1.5),
    right: theme.spacing(1.5),
    display: "flex",
    alignItems: "center",
  },
  root: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    backgroundColor: theme.palette.background.paper,
  },
  container: ({ isSidePanel }: StyleProps) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: isSidePanel ? "flex-start" : "center",
    justifyContent: "center",
    gap: theme.spacing(3),
    height: "100%",
    width: "100%",
    maxWidth: "1400px",
  }),
  subHeader: {
    fontWeight: 400,
  },
  inputContainer: ({ isWide }: StyleProps) => ({
    width: "100%",
    maxWidth: "689px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(3.5),
    ...styleIf(!isWide, {
      borderTop: theme.palette.constants.borderSmall,
      padding: theme.spacing(2),
      maxWidth: "none",
    }),
  }),
  headerContainer: ({ isSidePanel, isWide }: StyleProps) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
    textAlign: isSidePanel ? "left" : "center",
    padding: theme.spacing(0, 2),
    justifyContent: isSidePanel ? "flex-end" : "center",
    ...styleIf(!isWide, { flexGrow: 1 }),
    ...styleIf(isSidePanel, {
      width: "100%",
      alignItems: "stretch",
    }),
  }),
  fadeIn: {
    animation: "$fadeIn 0.5s",
  },
  // eslint-disable-next-line local-rules/disco-unused-classes
  "@keyframes fadeIn": {
    "0%": { opacity: 0 },
    "100%": { opacity: 1 },
  },
}))

export default Relay.withSkeleton({
  component: BrainSearchLandingPage,
  skeleton: () => <DiscoSpinner size={"lg"} absoluteCenter />,
})
